import React, { useMemo, useState, useEffect, useCallback } from "react";
import SearchFlightsNumber from "./SearchFlightNumber";
import SearchByRoute from "./SearchByRoute";
import { getRecentRequests, getUserLoadRequests } from "apis/myRequestsService";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { MyRequestParams, MyRequestResponse } from "@pages/YourRequest/types";
import FlightListing from "./LoadsFlightListing";
import HeroBg from "assets/images/top-main2.gif";

interface LoadsProps {
  readOnly?: boolean;
  breadcrumbLabel?: string;
  heroTitle?: React.ReactNode;
  heroSubtitle?: React.ReactNode;
}

const Loads: React.FC<LoadsProps> = ({
  readOnly = false,
  breadcrumbLabel = "Loads",
  heroTitle,
  heroSubtitle,
}) => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [activeTab, setActiveTab] = useState<"flightNumber" | "route">("route");
  const [flights, setFlights] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 5, // Limit to 5 flights
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);

  const handleTabSwitch = (tab: "flightNumber" | "route") => {
    setActiveTab(tab);
    setShowSearchResults(false);
  };

  const handleSearchResultsShow = (show: boolean) => {
    setShowSearchResults(show);
  };

  const fetchRecentFlights = useCallback(async () => {
    showLoader();
    setLoading(true);
    setError(null);

    try {
      const params: MyRequestParams = {
        page: 1,
        size: 5,
        sortBy: "createdDate",
        sortOrder: "desc",
      };
      const data = await getRecentRequests(params);
      setFlights(data);
    } catch (err) {
      console.error("Error fetching recent flights:", err);
    } finally {
      setLoading(false);
      setTimeout(() => hideLoader(), 500);
    }
  }, []);

  useEffect(() => {
    fetchRecentFlights();
  }, []);

  // Tab buttons - reused in both layouts
  const tabButtons = (
    <div className="flex items-end mb-0">
      <div className="flex items-center bg-[#1a2a30]/80 backdrop-blur-sm rounded-t-2xl overflow-hidden">
        {/* Flight No Tab */}
        <button
          role="tab"
          aria-selected={activeTab === "flightNumber"}
          aria-controls="flight-number-panel"
          className={`flex items-center gap-3 px-5 py-3 transition-all duration-200 rounded-t-2xl ${
            activeTab === "flightNumber"
              ? "bg-white text-[#1F2937]"
              : "text-gray-300 hover:text-white"
          }`}
          onClick={() => handleTabSwitch("flightNumber")}
          type="button"
        >
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-[#192227] ${
              activeTab === "flightNumber" ? "bg-[#FBBF24]" : "bg-[#656B6E]"
            }`}
          >
            <i
              className={`st-flight text-sm inline-block ${
                activeTab === "flightNumber" ? "text-[#192227]" : "text-[#FFFFFF]"
              }`}
              style={{ transform: "rotate(-50deg)" }}
            ></i>
          </span>
          <div className="flex flex-col items-start">
            <span
              className={`text-[10px] leading-tight ${activeTab === "flightNumber" ? "text-gray-400" : "text-gray-500"}`}
            >
              Search By
            </span>
            <span className="text-[13px] font-bold leading-tight">
              Flight No.
            </span>
          </div>
        </button>

        {/* Route Tab */}
        <button
          role="tab"
          aria-selected={activeTab === "route"}
          aria-controls="route-panel"
          className={`flex items-center gap-3 px-5 py-3 transition-all duration-200 rounded-t-2xl ${
            activeTab === "route"
              ? "bg-white text-[#1F2937]"
              : "text-gray-300 hover:text-white"
          }`}
          onClick={() => handleTabSwitch("route")}
          type="button"
        >
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-lg border border-[#192227] ${
              activeTab === "route" ? "bg-[#FBBF24]" : "bg-[#656B6E]"
            }`}
          >
            <i
              className={`st-route text-sm rotate-90 inline-block ${
                activeTab === "route" ? "text-[#192227]" : "text-[#FFFFFF]"
              }`}
            ></i>
          </span>
          <div className="flex flex-col items-start">
            <span
              className={`text-[10px] leading-tight ${activeTab === "route" ? "text-gray-400" : "text-gray-500"}`}
            >
              Search By
            </span>
            <span className="text-[13px] font-bold leading-tight">Route</span>
          </div>
        </button>
      </div>
    </div>
  );

  const activeContent = useMemo(
    () =>
      activeTab === "flightNumber" ? (
        <section className="mt-0">
          <SearchFlightsNumber
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={fetchRecentFlights}
            tabButtons={tabButtons}
            onBack={() => setShowSearchResults(false)}
            readOnly={readOnly}
            breadcrumbLabel={breadcrumbLabel}
          />
        </section>
      ) : (
        <section className="mt-0">
          <SearchByRoute
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={fetchRecentFlights}
            tabButtons={tabButtons}
            onBack={() => setShowSearchResults(false)}
            readOnly={readOnly}
            breadcrumbLabel={breadcrumbLabel}
          />
        </section>
      ),
    [activeTab, fetchRecentFlights, readOnly, breadcrumbLabel],
  );

  return (
    <div className="-m-6 relative">
      {/* Hero background — only show in default (non-results) mode */}
      {!showSearchResults && (
        <div
          className="absolute top-0 left-0 right-0 h-[470px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HeroBg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        </div>
      )}

      {/* All content flows naturally on top of the bg */}
      <div className="relative z-20">
        {/* Breadcrumb moved into child component's left column */}

        {/* Hero text (default mode only) */}
        {!showSearchResults && (
          <div className="flex flex-col items-center justify-center pt-12 pb-8">
            <h1 className="text-white text-4xl md:text-3xl font-semibold text-center leading-tight">
              {heroTitle ?? (
                <>
                  Flying <span className="text-[#FBBF24] italic">standby</span>?
                </>
              )}
            </h1>
            <p className="text-white text-4xl md:text-5xl font-bold text-center mt-2">
              {heroSubtitle ?? "Find your next flight."}
            </p>
          </div>
        )}

        {/*
          Tabs + search content — ALWAYS in the same tree position
          so React preserves component state across layout changes.
        */}
        <div
          className={`mx-auto ${
            showSearchResults ? "px-6 mt-0" : "max-w-[1200px] px-16"
          }`}
        >
          {activeContent}
        </div>
      </div>

      {error && <p className="text-red-500 mt-4 px-6">{error}</p>}

      {/* Recently Posted Requests (default mode only, hidden in read-only) */}
      {!showSearchResults && !readOnly && (
        <div className="relative z-10 px-6 mt-6">
          <FlightListing
            flights={flights}
            show={showSearchResults}
            FlightLoading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default Loads;
